import React, { useState, useRef, useEffect } from 'react';
import DropdownMenu, { DropdownMenuItem } from '../../DropdownMenu/DropdownMenu';
import './FormDialog.css';

/** 表单项-输入框类型 */
interface FormItemInput {
  type: 'input';
  /** 表单项唯一标识 */
  id: string;
  /** 表单项标题 */
  label: string;
  /** 输入框占位符 */
  placeholder?: string;
  /** 默认值 */
  defaultValue?: string;
  /** 是否必填 */
  required?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 输入框类型(text/password/number等) */
  inputType?: string;
}

/** 表单项-下拉选择框类型 */
interface FormItemSelect {
  type: 'select';
  /** 表单项唯一标识 */
  id: string;
  /** 表单项标题 */
  label: string;
  /** 下拉选项列表 */
  options: DropdownMenuItem[];
  /** 默认选中值 */
  defaultValue?: string | number;
  /** 是否必填 */
  required?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 占位符文本 */
  placeholder?: string;
}

/** 表单项联合类型 */
export type FormItem = FormItemInput | FormItemSelect;

/** 操作按钮配置 */
export interface FormDialogAction {
  /** 按钮文本 */
  label: string;
  /** 点击处理函数，返回 false 可阻止对话框关闭 */
  onClick: (formData: Record<string, string | number>) => boolean | void;
  /** 按钮类型，primary为主要按钮，secondary为次要按钮 */
  type?: 'primary' | 'secondary';
}

/** FormDialog 组件属性 */
export interface FormDialogProps {
  /** 是否显示对话框 */
  visible: boolean;
  /** 对话框标题 */
  title: string;
  /** 表单项列表 */
  formItems: FormItem[];
  /** 提示语(可选) */
  message?: string;
  /** 操作按钮数组 */
  actions: FormDialogAction[];
  /** 关闭对话框回调，点击遮罩层时触发 */
  onClose?: () => void;
}

/**
 * 通用表单对话框组件
 * 
 * 支持输入框和下拉选择框，可自定义表单项和操作按钮
 * 
 * @example
 * <FormDialog
 *   visible={dialogVisible}
 *   title="创建新影片"
 *   formItems={[
 *     { type: 'input', id: 'name', label: '影片名称', placeholder: '请输入影片名称', required: true },
 *     { type: 'select', id: 'type', label: '影片类型', options: typeOptions, defaultValue: 'movie' }
 *   ]}
 *   message="请填写影片基本信息"
 *   actions={[
 *     { label: "取消", onClick: handleCancel, type: "secondary" },
 *     { label: "创建", onClick: handleCreate, type: "primary" }
 *   ]}
 *   onClose={handleClose}
 * />
 */
const FormDialog: React.FC<FormDialogProps> = ({
  visible,
  title,
  formItems,
  message,
  actions,
  onClose
}) => {
  // 表单数据状态
  const [formData, setFormData] = useState<Record<string, string | number>>({});
  // 下拉菜单显示状态
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  // 下拉按钮引用
  const selectButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // 初始化表单数据
  useEffect(() => {
    if (visible) {
      const initialData: Record<string, string | number> = {};
      formItems.forEach(item => {
        if (item.type === 'input') {
          initialData[item.id] = item.defaultValue || '';
        } else if (item.type === 'select') {
          initialData[item.id] = item.defaultValue || '';
        }
      });
      setFormData(initialData);
      setOpenDropdown(null);
    }
  }, [visible, formItems]);

  if (!visible) {
    return null;
  }

  // 处理遮罩层点击
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  // 处理输入框变化
  const handleInputChange = (id: string, value: string) => {
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  // 处理下拉选择
  const handleSelectChange = (id: string, item: DropdownMenuItem) => {
    setFormData(prev => ({ ...prev, [id]: item.id }));
    setOpenDropdown(null);
  };

  // 处理按钮点击
  const handleActionClick = (action: FormDialogAction) => {
    const result = action.onClick(formData);
    // 如果返回 false，不关闭对话框
    if (result !== false && onClose) {
      onClose();
    }
  };

  // 获取下拉菜单位置
  const getDropdownPosition = (id: string) => {
    const buttonRef = selectButtonRefs.current[id];
    if (!buttonRef) return {};

    const rect = buttonRef.getBoundingClientRect();
    return {
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width
    };
  };

  return (
    <div className="form-dialog-overlay" onClick={handleOverlayClick}>
      <div className="form-dialog">
        {/* 标题 */}
        <div className="form-dialog-header">
          <h3>{title}</h3>
        </div>
        
        {/* 表单内容 */}
        <div className="form-dialog-content">
          {/* 表单项列表 */}
          <div className="form-dialog-items">
            {formItems.map(item => (
              <div key={item.id} className="form-dialog-item">
                {/* 表单项标题 */}
                <label className="form-dialog-item-label">
                  {item.label}
                  {item.required && <span className="form-dialog-item-required">*</span>}
                </label>

                {/* 表单项输入区域 */}
                <div className="form-dialog-item-input">
                  {item.type === 'input' ? (
                    // 输入框
                    <input
                      type={item.inputType || 'text'}
                      className="form-dialog-input"
                      placeholder={item.placeholder}
                      value={formData[item.id] || ''}
                      onChange={(e) => handleInputChange(item.id, e.target.value)}
                      disabled={item.disabled}
                    />
                  ) : (
                    // 下拉选择框
                    <>
                      <button
                        ref={(el) => { selectButtonRefs.current[item.id] = el; }}
                        className={`form-dialog-select-button ${item.disabled ? 'form-dialog-select-button-disabled' : ''}`}
                        onClick={() => !item.disabled && setOpenDropdown(openDropdown === item.id ? null : item.id)}
                        disabled={item.disabled}
                      >
                        <span className="form-dialog-select-text">
                          {formData[item.id] 
                            ? item.options.find(opt => opt.id === formData[item.id])?.label 
                            : item.placeholder || '请选择'}
                        </span>
                        <span className="form-dialog-select-arrow">▼</span>
                      </button>

                      {/* 下拉菜单 */}
                      {openDropdown === item.id && (
                        <DropdownMenu
                          items={item.options}
                          visible={true}
                          onClose={() => setOpenDropdown(null)}
                          onSelect={(selectedItem) => handleSelectChange(item.id, selectedItem)}
                          position={getDropdownPosition(item.id)}
                          useFixedPosition={true}
                          selectedValue={formData[item.id]}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* 提示语区域 - 固定高度 */}
          <div className="form-dialog-message-container">
            {message && (
              <div className="form-dialog-message">
                {message}
              </div>
            )}
          </div>
        </div>
        
        {/* 操作按钮 */}
        <div className="form-dialog-actions">
          {actions.map((action, index) => (
            <button
              key={index}
              className={`form-dialog-btn form-dialog-btn-${action.type || 'secondary'}`}
              onClick={() => handleActionClick(action)}
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FormDialog;
